/**
 * Visualização do Mapa Estratégico da Cliente.
 * Território Creme (Client). Modo Read-Only com Motor Astral responsivo.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { api } from "../../lib/api";
import { useSession } from "../../lib/session";
import { AstralCanvas } from "../../components/astral/AstralCanvas";
import type { AstralMapData } from "../../types/astral";
import { BrandMark } from "../../ui/BrandMark";
import { Button } from "../../ui/Button";
import { EmptyState } from "../../ui/Card";
import { FullScreenLoader } from "../../ui/ProtectedRoute";

interface ClientMapResponse {
  id: string;
  title: string;
  layout: string;
  theme: string;
  astralData: AstralMapData;
  updatedAt: string;
}

export function MeuMapa() {
  const navigate = useNavigate();
  const { session, setSession } = useSession();
  const [loading, setLoading] = useState(true);
  const [mapInfo, setMapInfo] = useState<ClientMapResponse | null>(null);
  const [mapData, setMapData] = useState<AstralMapData | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .get<ClientMapResponse | null>("/approval/map")
      .then((res) => {
        if (!active) return;
        if (res && res.astralData) {
          setMapInfo(res);
          setMapData(res.astralData);
        } else {
          setMapInfo(null);
          setMapData(null);
        }
      })
      .catch(() => {
        if (active) {
          setMapInfo(null);
          setMapData(null);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  function toggleFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
      setFullscreen(true);
    } else {
      document.exitFullscreen?.().catch(() => {});
      setFullscreen(false);
    }
  }

  if (loading) {
    return <FullScreenLoader />;
  }

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-surface-canvas text-text-primary">
      {/* Barra superior de navegação da cliente */}
      <header className="z-20 flex h-14 shrink-0 items-center justify-between border-b border-border-default bg-surface-elevated/90 px-4 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <Link
            to="/aprovacoes"
            className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-control-sm)] border border-border-default bg-surface-sunken text-sm text-text-secondary transition hover:bg-surface-elevated hover:text-text-primary"
            title="Voltar para Aprovações"
          >
            ←
          </Link>
          <div className="flex items-center gap-2">
            <BrandMark variant="vinho" className="h-6" />
            <div className="hidden sm:block">
              <h1 className="text-sm font-semibold leading-tight text-text-primary">
                {mapInfo?.title || "Meu Mapa Estratégico"}
              </h1>
              <p className="text-[10px] text-text-tertiary">
                {session?.name ? `Metodologia Lara para ${session.name}` : "Posicionamento Estratégico Unik"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={toggleFullscreen}
            className="hidden sm:inline-flex text-xs"
            title="Alternar tela cheia"
          >
            {fullscreen ? "Sair da Tela Cheia" : "Tela Cheia"}
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              await api.post("/auth/logout").catch(() => {});
              setSession(null);
              navigate("/entrar", { replace: true });
            }}
            className="text-xs text-text-tertiary hover:text-danger"
          >
            Sair
          </Button>
        </div>
      </header>

      {/* Área principal do Canvas Astral */}
      <main className="relative flex-1 overflow-hidden bg-surface-canvas">
        {mapData ? (
          <AstralCanvas
            mapData={mapData}
            readOnly={true}
            autoFitOnMount={true}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center p-6">
            <div className="max-w-md">
              <EmptyState
                title="Mapa Estratégico em Construção"
                description="A equipe da Unik está desenhando seu plano de posicionamento personalizado. Assim que estiver pronto, você poderá navegar por cada ramo aqui!"
              />
              <div className="mt-4 flex justify-center">
                <Link to="/aprovacoes">
                  <Button size="sm">Voltar para Aprovações</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
